import { inject, Injectable } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PaceInsightsService } from './pace-insights.service';
import { delay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ModalTelemetryService {
    private paceInsightsService = inject(PaceInsightsService);
    private modalService = inject(BsModalService);

    public init(): void {
        if (this.paceInsightsService.isInsightsEnabled()) {
            this.modalService.onShow.pipe(delay(500)).subscribe(() => this.collectMetaInformation('show'));
            this.modalService.onShown.pipe(delay(500)).subscribe(() => this.collectMetaInformation('shown'));
            this.modalService.onHide.pipe(delay(500)).subscribe(() => this.collectMetaInformation('hide'));
            this.modalService.onHidden.pipe(delay(500)).subscribe(() => this.collectMetaInformation('hidden'));
        }
    }

    private collectMetaInformation(phase: string): void {
        const host = this.getFirstModalHost();
        const meta = host ? this.extractMeta(host) : undefined;

        this.paceInsightsService.trackEvent('ModalEvent', {
            phase,
            ...meta,
        });
    }

    private getFirstModalHost(): HTMLElement | null {
        const contents = document.querySelectorAll('.modal.show .modal-content');
        if (!contents?.length) {
            return null;
        }
        const topmost = contents[contents.length - 1] as HTMLElement | undefined;
        const host = topmost?.firstElementChild as HTMLElement | null | undefined;
        return host ?? null;
    }

    private extractMeta(host?: HTMLElement): Record<string, string> | undefined {
        if (!host) {
            return undefined;
        }
        const tag = host.tagName;

        const dialog = host.closest('.modal-dialog');
        const allCls = [
            dialog?.className ?? '',
            host.parentElement?.className ?? '', // .modal-content
        ].join(' ');

        let size = 'UNKNOWN';

        if (/\bmodal-sm\b/i.test(allCls)) {
            size = 'SMALL';
        } else if (/\bmodal-lg\b/i.test(allCls)) {
            size = 'MEDIUM';
        } else if (/\bmodal-xl\b/i.test(allCls)) {
            size = 'LARGE';
        }

        const modalRoot = host.closest('.modal');
        const labelledBy = modalRoot?.getAttribute('aria-labelledby') || '';
        const title = this.safeShortText(labelledBy);

        return {
            component: tag,
            size,
            ariaLabelledBy: labelledBy,
            title,
        };
    }

    private safeShortText(elementId?: string | null): string {
        if (!elementId) {
            return '';
        }
        const el = document.getElementById(elementId);
        if (!el) {
            return '';
        }
        const t = (el.textContent || '').trim().replace(/\s+/g, ' ');
        return t.length > 120 ? t.slice(0, 117) + '...' : t;
    }
}
